import type { PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { Card } from "~/features/configurator/Linear/components/Card";
import { type ReactNode, useMemo } from "react";
import { Box, Divider, FormControlLabel, Radio, Stack, Typography, styled } from "~/common/mui";
import { getStockLevel, StockIndicatorIcon } from "~/domains/product/components/StockIndicator/StockIndicator";
import { CountryOfOriginDescriptorParam } from "~/domains/product/components/ProductParams";
import { useTranslation } from "react-i18next";
import { getPackagingLabel } from "~/features/cart/cart/utils/cartUtils";
import dayjs from "dayjs";
import { useIsPackagePhasedOut } from "~/features/productDetails/components/PhaseInOut/usePhaseInOutLogic";
import { DiscontinuationNotification } from "~/features/productDetails/components/PhaseInOut/DiscontinuationNotification/DiscontinuationNotification";
import { type PackagingSelectionVariant } from "~/domains/product/components/PackagingSelection/PackagingSelectionVariant";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";

interface Props {
  className?: string;
  pack: PackagingInformationEntry;
  productDescriptor: ProductDescriptor;
  selected?: boolean;
  onSelect?: () => void;
  variant: PackagingSelectionVariant;
  disabled?: boolean;
}

const CARD_PADDING = 12;

// Styled components
const Root = styled(Card)(({ theme }) => ({
  "&&": {
    background: theme.palette.common.white,
    padding: 0,
    display: "flex",
    flexFlow: "column",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      border: "none !important",
    },
  },
}));

const CardContentStyled = styled(Box)(({ theme }) => ({
  padding: CARD_PADDING,
  display: "flex",
  flexFlow: "column",
  "&:empty": {
    display: "none",
  },
}));

const PackageCard = styled(CardContentStyled, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  background: isSelected ? theme.palette.secondary.container : theme.palette.surface.bright,
  "&:hover, &:focus": {
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.palette.text.primary,
      opacity: isSelected ? 0.08 : 0.12,
    },
  },
}));

const StockIcon = styled(StockIndicatorIcon)({
  "--size": "8px",
  marginRight: 0,
});

const StockContent = styled(Box)({
  display: "grid",
  gridTemplateColumns: "35% 45% 20%",
  width: "100%",
  alignItems: "center",
});

export const NewPackagingSelectionItem = ({ selected, onSelect, productDescriptor, pack, disabled }: Props) => {
  const { t } = useTranslation();

  const packagingLabel = getPackagingLabel({
    t,
    productDescriptor,
    packSize: pack.packSize,
    packCode: pack.packCode,
  });

  const showDiscontinuedWarning = useIsPackagePhasedOut(productDescriptor, pack.packCode);
  const stockCalculations = useInStockCalculations(pack);

  const discontinuationNotification = showDiscontinuedWarning && (
    <DiscontinuationNotification discontinued={productDescriptor} variant={"package"} />
  );

  const isPackageOnSale = useSalesHelper(productDescriptor).isPackageOnSale(pack.packCode);

  const header = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <FormControlLabel
        key={pack.packCode}
        value={pack.packCode}
        checked={selected}
        control={<Radio sx={{ my: "-8px" }} color="onSecondaryContainer" />}
        label={<Typography variant="titleMedium">{packagingLabel}</Typography>}
        onChange={onSelect}
        disabled={disabled}
      />
      {isPackageOnSale && <SaleTag size={16} />}
    </Box>
  );

  const stockContent = (
    <StockContent>
      <StockIndicatorValue totalInStock={stockCalculations.totalInStock} />
      <InProductionSection pack={pack} />
      <CountryOfOriginDescriptorParam
        showEmpty
        descriptor={productDescriptor}
        packCode={pack.packCode}
        withoutLabel={true}
      />
    </StockContent>
  );

  const handleClickOnPackage = () => {
    if (onSelect) {
      onSelect();
    }
  };

  return (
    <Root active={selected} onClick={handleClickOnPackage}>
      <PackageCard isSelected={selected}>
        {discontinuationNotification}
        <Stack
          divider={<Divider sx={(theme) => ({ color: selected ? theme.palette.divider : theme.palette.divider })} />}
          gap={1}
        >
          {header}
          {stockContent}
        </Stack>
      </PackageCard>
    </Root>
  );
};

const getCompactNumber = (value: number) => {
  const absValue = Math.abs(value);

  // If a number has more than 5 digits, round it up to the nearest thousand
  if (absValue < 10000) {
    return NumberFormatter.getDefaultFormatter().format(value);
  } else {
    const rounded = Math.round(absValue / 1000);
    return absValue > rounded * 1000 ? `${rounded}k+` : `${rounded}k`;
  }
};

const useInStockCalculations = (pack: PackagingInformationEntry) => {
  const { t } = useTranslation();
  return useMemo(() => {
    const readyForShipment = pack.totalWarehouse.inStock.find((entry) => !entry.date)?.quantity ?? 0;
    const inStockEntries = pack.totalWarehouse.inStock.map(
      (entry) =>
        [
          entry.quantity,
          entry.date
            ? dayjs(entry.date).format("DD.MM.YYYY")
            : t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" }),
        ] as const,
    );
    const totalInStock = inStockEntries.reduce((acc, [quantity]) => acc + quantity, 0);
    return { readyForShipment, inStockEntries, totalInStock };
  }, [pack]);
};

const StockIndicatorValue = ({ totalInStock }: { totalInStock: number }) => {
  const { t } = useTranslation();

  return (
    <Stack direction={"row"} gap={1} sx={{ display: "flex", alignItems: "center" }}>
      <StockIcon level={getStockLevel(totalInStock)} />
      <p>
        {totalInStock > 0 && t("stockIndicator.existsWithQty", { amount: getCompactNumber(totalInStock) })}
        {totalInStock === 0 && t("addToCartPopup.stock.info")}
      </p>
    </Stack>
  );
};

const InProductionSection = ({ pack }: { pack: PackagingInformationEntry }) => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();

  const inProductionEntries = pack.totalWarehouse.inProduction.map(
    (entry) => [entry.quantity, dayjs(entry.date).format("DD.MM.YYYY")] as const,
  );

  if (pack.totalWarehouse.inClarification > 0) {
    inProductionEntries.unshift([
      pack.totalWarehouse.inClarification,
      t("product.availability.stock.table.inClarification"),
    ] as const);
  }
  const totalInProduction = inProductionEntries.reduce((acc, [quantity]) => acc + quantity, 0);
  return (
    <>
      <Stack
        direction={"row"}
        gap={1}
        sx={{ display: "flex", alignItems: "center", visibility: totalInProduction <= 0 ? "hidden" : "visible" }}
      >
        <CalendarTodayOutlinedIcon fontSize="small" />
        {!isTablet ? (
          <Typography>{getCompactNumber(totalInProduction)}</Typography>
        ) : (
          <Typography>
            {t("productDetailsPage.availabilityTable.toCome", {
              quantity: getCompactNumber(totalInProduction),
            })}
          </Typography>
        )}
      </Stack>
    </>
  );
};
